import { Plus } from "lucide-react";
import LocalErrandCard from "./LocalErrandCard.jsx";
import TripListingCard from "./TripListingCard.jsx";

export default function MarketplaceListings({
  role,
  userId,
  marketplaceToday,
  search,
  onSearchChange,
  date,
  onDateChange,
  hasFilter,
  onClearFilters,
  loaded,
  trips,
  filteredTrips,
  tripsLoading,
  tripsError,
  onRetryTrips,
  tripsMoreError,
  tripsHasMore,
  tripsMoreLoading,
  onLoadMoreTrips,
  offers,
  filteredOffers,
  offersLoading,
  offersError,
  onRetryOffers,
  offersMoreError,
  offersHasMore,
  offersMoreLoading,
  onLoadMoreOffers,
  ownerProfiles,
  ownerProfilesError,
  onRetryOwnerProfiles,
  reputationSummary,
  onPublishTrip,
  onPublishErrand,
  onRequestTrip,
  onRequestOffer,
  onDeactivateTrip,
  onDeactivateOffer,
  onOpenReviews,
}) {
  return (
    <>
      <div className="hero">
        <div>
          <h1>
            {role === "traveler"
              ? "Publish your next trip."
              : "Find a trusted traveler."}
          </h1>
          <p>
            Protected requests, realtime updates, and private proof uploads.
          </p>
        </div>
        {role === "traveler" && (
          <div className="publish-actions">
            <button type="button" onClick={onPublishTrip}>
              <Plus size={18} />
              Post trip
            </button>
            <button type="button" className="secondary" onClick={onPublishErrand}>
              <Plus size={18} />
              Offer local errand
            </button>
          </div>
        )}
      </div>
      <section
        className="marketplace-filters"
        aria-label="Search marketplace"
      >
        <label htmlFor="marketplace-search">
          Search trips, stores, or areas
          <input
            id="marketplace-search"
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="e.g. Baguio, grocery, Paranaque"
          />
        </label>
        <label htmlFor="marketplace-date">
          Travel or errand date
          <input
            id="marketplace-date"
            type="date"
            min={marketplaceToday}
            value={date}
            onChange={(event) => onDateChange(event.target.value)}
          />
        </label>
        {hasFilter && (
          <button type="button" onClick={onClearFilters}>
            Clear filters
          </button>
        )}
      </section>
      <p className="section-intro">
        Trips and local errands stay available through their scheduled date in
        Philippines time, then expire automatically. Existing orders remain
        available to their participants.
      </p>
      {ownerProfilesError && (
        <p className="marketplace-load-error" role="alert">
          Could not load traveler profiles: {ownerProfilesError}{" "}
          <button type="button" onClick={onRetryOwnerProfiles}>
            Retry
          </button>
        </p>
      )}
      {loaded &&
        !tripsLoading &&
        !offersLoading &&
        !tripsError &&
        !offersError && (
          <p
            className="marketplace-results"
            role="status"
            aria-live="polite"
          >
            Showing {filteredTrips.length} matching trips among {trips.length}{" "}
            loaded and {filteredOffers.length} matching local errands among{" "}
            {offers.length} loaded.
            {(tripsHasMore || offersHasMore) && hasFilter
              ? " Load more to search additional listings."
              : ""}
          </p>
        )}
      <h2>Available trips</h2>
      {tripsLoading || !loaded ? (
        <p className="marketplace-empty" role="status" aria-live="polite">
          Loading trips…
        </p>
      ) : tripsError ? (
        <p className="marketplace-load-error" role="alert">
          Could not load trips. {tripsError}{" "}
          <button type="button" onClick={onRetryTrips}>
            Retry
          </button>
        </p>
      ) : trips.length === 0 || (filteredTrips.length === 0 && !hasFilter) ? (
        <p className="marketplace-empty" role="status">
          No trips are available right now.
        </p>
      ) : filteredTrips.length === 0 ? (
        <p className="marketplace-empty" role="status">
          No loaded trips match these filters.
        </p>
      ) : (
        <div className="grid">
          {filteredTrips.map((trip) => (
            <TripListingCard
              key={trip.id}
              trip={trip}
              traveler={ownerProfiles[trip.owner_id]}
              reputation={reputationSummary(trip.owner_id)}
              isBuyer={role === "buyer"}
              isOwner={trip.owner_id === userId}
              onRequest={() => onRequestTrip(trip)}
              onDeactivate={() => onDeactivateTrip(trip)}
              onOpenReviews={
                onOpenReviews
                  ? () => onOpenReviews(trip.owner_id)
                  : undefined
              }
            />
          ))}
        </div>
      )}
      {tripsMoreError && (
        <p className="marketplace-load-error" role="alert">
          Could not load more trips. {tripsMoreError}{" "}
          <button type="button" onClick={onLoadMoreTrips}>
            Retry
          </button>
        </p>
      )}
      {tripsHasMore && (
        <button
          className="marketplace-load-more"
          type="button"
          disabled={tripsMoreLoading || tripsLoading}
          onClick={onLoadMoreTrips}
        >
          {tripsMoreLoading ? "Loading more trips…" : "Load more trips"}
        </button>
      )}
      <section
        className="local-errands"
        aria-labelledby="local-errands-heading"
      >
        <h2 className="section-heading" id="local-errands-heading">
          Nearby store runs
        </h2>
        <p className="section-intro">
          Short local errands planned around local areas and stores.
        </p>
        {offersLoading || !loaded ? (
          <p className="empty-inline" role="status" aria-live="polite">
            Loading local errand offers…
          </p>
        ) : offersError ? (
          <p className="marketplace-load-error" role="alert">
            Could not load local errand offers. {offersError}{" "}
            <button type="button" onClick={onRetryOffers}>
              Retry
            </button>
          </p>
        ) : offers.length === 0 ||
          (filteredOffers.length === 0 && !hasFilter) ? (
          <p className="empty-inline" role="status">
            {role === "buyer"
              ? "No nearby store runs in your city and barangay right now. Check back later or try another date."
              : "No active local errand offers right now."}
          </p>
        ) : filteredOffers.length === 0 ? (
          <p className="marketplace-empty" role="status">
            {role === "buyer"
              ? "No nearby local errands in your city and barangay match these filters."
              : "No loaded local errands match these filters."}
          </p>
        ) : (
          <div className="grid">
            {filteredOffers.map((offer) => (
              <LocalErrandCard
                key={offer.id}
                offer={offer}
                traveler={ownerProfiles[offer.owner_id]}
                reputation={reputationSummary(offer.owner_id)}
                isBuyer={role === "buyer"}
                isOwner={offer.owner_id === userId}
                onRequest={() => onRequestOffer(offer)}
                onDeactivate={() => onDeactivateOffer(offer)}
                onOpenReviews={
                  onOpenReviews
                    ? () => onOpenReviews(offer.owner_id)
                    : undefined
                }
              />
            ))}
          </div>
        )}
        {offersMoreError && (
          <p className="marketplace-load-error" role="alert">
            Could not load more local errands. {offersMoreError}{" "}
            <button type="button" onClick={onLoadMoreOffers}>
              Retry
            </button>
          </p>
        )}
        {offersHasMore && (
          <button
            className="marketplace-load-more"
            type="button"
            disabled={offersMoreLoading || offersLoading}
            onClick={onLoadMoreOffers}
          >
            {offersMoreLoading
              ? "Loading more local errands…"
              : "Load more local errands"}
          </button>
        )}
      </section>
    </>
  );
}
